import { CheckCircle, MoreVert, Save } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { type ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useCornTechnologies } from '/@/hooks/catalog';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useGetUserNode } from '/@/hooks/nodes';
import { useProducts } from '/@/hooks/recommendation';
import { useReports } from '/@/hooks/reports';
import { _ } from '/@/locale';
import { getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { type CropMonitoringFormType, MAX_ENVIRONMENT, MIN_ENVIRONMENT } from '../../../creation-dialog';
import DensityInput from '../../../creation-dialog/components/DensityInput';
import EnvironmentInput from '../../../creation-dialog/components/EnvironmentInput';
import PlantedAreaInput from '../../../creation-dialog/components/PlantedAreaInput';
import PlantingDateInput from '../../../creation-dialog/components/PlantingDateInput';
import ProductSelector from '../../../creation-dialog/components/ProductSelector';
import TechnologySelector from '../../../creation-dialog/components/TechnologySelector';
import { FIELDVIEW_CROP_MAP } from '../../../creation-dialog/utils';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { COLUMNS } from './data-grid';
import { EditActions } from './edit-actions';
import { normalizeProductName } from './recommendation-helpers';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';

const Cell = ({ title, value }: { title: string; value: string | number }) => {
    return (
        <Stack direction='column' spacing={0.5} sx={{ p: 1, backgroundColor: 'grey.100' }}>
            <Typography variant='caption' color='primary'>
                {title}
            </Typography>
            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                {value}
            </Typography>
        </Stack>
    );
};

const EditCell = ({ children }: { children: ReactNode }) => (
    <Box sx={{ p: 1, backgroundColor: 'grey.100' }}>{children}</Box>
);

const buildFormDefaults = (cropMonitoring: CropMonitoring): CropMonitoringFormType => ({
    plantingDate: cropMonitoring.plantingDate,
    product: cropMonitoring?.cornCropMonitoring?.product ?? cropMonitoring?.productName,
    technology: cropMonitoring.cornCropMonitoring?.technology,
    environment: cropMonitoring.environment,
    plantedArea: cropMonitoring.plantedArea ?? cropMonitoring.result?.plantedArea ?? undefined,
    density: cropMonitoring.density ?? undefined,
    fieldId: cropMonitoring.field,
    id: cropMonitoring.id,
    crop: cropMonitoring.crop,
    previousCrop: cropMonitoring.previousCrop ?? '',
});

const RegistryPlanting = ({
    cropMonitoring,
    notes,
    recommendedProductNames,
}: {
    cropMonitoring: CropMonitoring;
    notes: CropMonitoringNote[];
    /** Normalized names of products recommended for the campaign — flags a followed reco. */
    recommendedProductNames?: Set<string>;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [editing, setEditing] = useState(false);
    const form = useForm<CropMonitoringFormType>({
        defaultValues: buildFormDefaults(cropMonitoring),
    });

    const units = useUnitProvider();
    const country = useSelector(getSessionCountry);
    const sessionBrands = useSelector(getSessionBrands);

    const fieldQuery = useFieldById({ id: cropMonitoring.field }, { enabled: !!cropMonitoring.field });

    const technologiesQuery = useCornTechnologies({
        page: 1,
        pageSize: 10000,
        brand: sessionBrands.corn?.[0]?.codename,
        country: country?.code,
    });
    const technologyName = (technologiesQuery.data?.results as { id: number; name?: string }[] | undefined)?.find(
        (t) => t.id === cropMonitoring.cornCropMonitoring?.technology
    )?.name;

    const fromFieldview = !!cropMonitoring.sourcePlantingReport;
    const hasGenericProduct = !!cropMonitoring.cornCropMonitoring?.hasGenericProduct;

    const plantingReportsQuery = useReports(
        { fieldId: cropMonitoring.field, type: 'planting' },
        { enabled: fromFieldview && hasGenericProduct }
    );
    const fieldviewVariety = useMemo(() => {
        if (!cropMonitoring.sourcePlantingReport || !plantingReportsQuery.data) return undefined;
        const mergedResults = (
            plantingReportsQuery.data.results as {
                mergedResults: { report: number; cropName: string; seedProductNames: string[] }[];
            }[]
        ).flatMap((r) => r.mergedResults ?? []);
        const match = mergedResults.find(
            (r) =>
                r.report === cropMonitoring.sourcePlantingReport &&
                FIELDVIEW_CROP_MAP[r.cropName.toLowerCase()] === cropMonitoring.crop
        );
        return match?.seedProductNames[0];
    }, [cropMonitoring.sourcePlantingReport, cropMonitoring.crop, plantingReportsQuery.data]);

    const closeMenu = () => setMenuAnchor(null);

    const reachedV6 = cropMonitoring.cornCropMonitoring?.stages.some((stage) => (stage.vegetativeStage || 0) >= 6);

    const startEditing = () => {
        closeMenu();
        form.reset(buildFormDefaults(cropMonitoring));
        setEditing(true);
    };

    const cancelEditing = () => setEditing(false);

    const { plantingDate, environment, plantedArea, density, product, technology } = form.watch();

    const debouncedEnvironment = useDebounce(environment, 1000);
    const productsQuery = useProducts(
        {
            lat: fieldQuery.data?.lat?.toString() || '',
            lng: fieldQuery.data?.lng?.toString() || '',
            crop: cropMonitoring.crop || 'corn',
            plantingDate: plantingDate ? dayjs(plantingDate).format('YYYY-MM-DD') : '',
            brandCodename: sessionBrands[cropMonitoring.crop || 'corn']?.[0]?.codename || '',
            environment: debouncedEnvironment?.toString() || '',
        },
        { enabled: editing }
    );

    const [convertedMinEnv, convertedMaxEnv] = useMemo(
        () => [units.toYield(MIN_ENVIRONMENT, 'corn', 2).value, units.toYield(MAX_ENVIRONMENT, 'corn', 2).value],
        [units]
    );
    const isProductDisabled = useMemo(() => {
        if (!environment || !plantingDate) return true;
        const env = units.toYield(environment, 'corn', 2).value;
        return env < convertedMinEnv || env > convertedMaxEnv;
    }, [environment, plantingDate, units, convertedMinEnv, convertedMaxEnv]);
    const isTechnologyDisabled = isProductDisabled || !product;

    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            plantingDate,
            environment: environment || 0,
            plantedArea,
            density,
            field: cropMonitoring.field,
            technology,
            product,
            crop: cropMonitoring.crop || undefined,
            previousCrop: cropMonitoring.previousCrop || undefined,
            id: cropMonitoring.id,
        },
        {
            onSuccess: () => setEditing(false),
        }
    );

    const isValid = Boolean(plantingDate && environment && product && (cropMonitoring.crop !== 'corn' || technology));

    const handleSave = async () => {
        if (!isValid) return;
        await createOrUpdateCropMonitoring();
    };

    const plantingDateDisplay = cropMonitoring.plantingDate ? formatDate(new Date(cropMonitoring.plantingDate)) : '—';
    const ambiente = cropMonitoring.environment
        ? units.toYield(cropMonitoring.environment, cropMonitoring.crop, 0).toString()
        : '—';
    const superficie =
        cropMonitoring.plantedArea != null
            ? units.toArea(cropMonitoring.plantedArea, 2).toString()
            : cropMonitoring.result?.plantedArea != null
              ? convertAreaFromM2(cropMonitoring.result.plantedArea, units).toString()
              : cropMonitoring.fieldArea != null
                ? convertAreaFromM2(Number(cropMonitoring.fieldArea), units).toString()
                : '—';
    const densidad =
        cropMonitoring.density != null
            ? units.toDensity(cropMonitoring.density, cropMonitoring.crop, 0).toString()
            : '—';
    const registeredAt = cropMonitoring.created ? formatDate(new Date(cropMonitoring.created)) : null;
    const followedRecommendation = Boolean(
        cropMonitoring.productName && recommendedProductNames?.has(normalizeProductName(cropMonitoring.productName))
    );
    const userNodeQuery = useGetUserNode(cropMonitoring.createdBy ?? 0);
    const authorName = getUserDisplayName(userNodeQuery.data);

    return (
        <RegistryCard>
            {/* Header */}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                    {fromFieldview && (
                        <Icon type='fieldview' style={{ fill: '#F7B618', width: '20px', height: '20px' }} />
                    )}
                    <Typography variant='h6' color='primary'>
                        {_('Siembra')}
                    </Typography>
                </Stack>
                <IconButton size='small' aria-label={_('Más opciones')} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreVert fontSize='small' />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                    <Tooltip
                        arrow
                        title={reachedV6 ? _('Este registro no se puede editar, el cultivo alcanzó V6.') : ''}
                    >
                        <span>
                            <MenuItem onClick={startEditing} disabled={editing || reachedV6}>
                                {_('Editar')}
                            </MenuItem>
                        </span>
                    </Tooltip>
                </Menu>
            </Stack>

            {hasGenericProduct && (
                <Alert severity='danger'>
                    {_(
                        'No pudimos identificar este híbrido dentro del modelo de GDUs. Los datos de fenología se calcularán con un modelo genérico.'
                    )}
                </Alert>
            )}

            {editing && fieldQuery.data ? (
                <Stack spacing={2}>
                    <Box
                        sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, overflow: 'auto', width: '100%' }}
                    >
                        {/* A FieldView planting owns its date, area and density — they come from
                            the report and the API rejects changing the date. Only product,
                            technology and environment are the user's to correct, so the rest stay
                            read-only in place rather than disappearing from the grid. */}
                        {fromFieldview ? (
                            <Cell title={_('Fecha de siembra')} value={plantingDateDisplay} />
                        ) : (
                            <EditCell>
                                <PlantingDateInput
                                    value={plantingDate}
                                    onChange={(date) => form.setValue('plantingDate', date, { shouldValidate: true })}
                                    field={fieldQuery.data}
                                    crop={cropMonitoring.crop}
                                />
                            </EditCell>
                        )}
                        <EditCell>
                            <ProductSelector
                                crop={cropMonitoring.crop}
                                value={product}
                                onChange={(value) => form.setValue('product', value, { shouldValidate: true })}
                                productsQuery={productsQuery}
                                disabled={isProductDisabled}
                                units={units}
                                minEnvironment={MIN_ENVIRONMENT}
                                maxEnvironment={MAX_ENVIRONMENT}
                                environment={environment}
                            />
                        </EditCell>
                        {cropMonitoring.crop === 'corn' && (
                            <EditCell>
                                <TechnologySelector
                                    value={technology}
                                    onChange={(value) => form.setValue('technology', value, { shouldValidate: true })}
                                    disabled={isTechnologyDisabled}
                                />
                            </EditCell>
                        )}
                        <EditCell>
                            <EnvironmentInput
                                crop={cropMonitoring.crop || 'corn'}
                                value={environment}
                                onChange={(value) => form.setValue('environment', value, { shouldValidate: true })}
                                units={units}
                                minEnvironment={MIN_ENVIRONMENT}
                                maxEnvironment={MAX_ENVIRONMENT}
                            />
                        </EditCell>
                        {fromFieldview ? (
                            <Cell title={_('Superficie sembrada')} value={superficie} />
                        ) : (
                            <EditCell>
                                <PlantedAreaInput
                                    value={plantedArea}
                                    onChange={(value) => form.setValue('plantedArea', value)}
                                    units={units}
                                />
                            </EditCell>
                        )}
                        {fromFieldview ? (
                            <Cell title={_('Densidad')} value={densidad} />
                        ) : (
                            <EditCell>
                                <DensityInput
                                    crop={cropMonitoring.crop || 'corn'}
                                    value={density}
                                    onChange={(value) => form.setValue('density', value)}
                                    units={units}
                                />
                            </EditCell>
                        )}
                    </Box>

                    <EditActions
                        onCancel={cancelEditing}
                        onSave={handleSave}
                        saveDisabled={!isValid || isMutating}
                        cancelDisabled={isMutating}
                        saveColor='secondary'
                        saveStartIcon={isMutating ? <CircularProgress size={18} /> : <Save fontSize='small' />}
                    />
                </Stack>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, overflow: 'auto', width: '100%' }}>
                    <Cell title={_('Fecha de siembra')} value={plantingDateDisplay} />
                    <Cell
                        title={_('Producto')}
                        value={(hasGenericProduct && fieldviewVariety) || cropMonitoring.productName || '—'}
                    />
                    <Cell title={_('Tecnología')} value={technologyName ?? '—'} />
                    <Cell title={_('Ambiente')} value={ambiente} />
                    <Cell title={_('Superficie sembrada')} value={superficie} />
                    <Cell title={_('Densidad')} value={densidad} />
                </Box>
            )}

            <RegistryNotes
                notes={notes}
                fieldId={cropMonitoring.field}
                plantingDateCropMonitoringId={cropMonitoring.id}
            />

            {followedRecommendation && (
                <Alert severity='blue' icon={<CheckCircle fontSize='inherit' />}>
                    {_('La siembra se realizó con el producto que se recomendó.')}
                </Alert>
            )}

            {/* Footer */}
            {fromFieldview ? (
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Sincronizado desde FieldView')}
                </Typography>
            ) : (
                registeredAt && <RegistryFooter authorName={authorName} date={registeredAt} />
            )}
        </RegistryCard>
    );
};

export default RegistryPlanting;
